import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d8j8b6kcl43d0bibf700';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === 'loading') return;
    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50/80 pt-16 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10 mb-12">
          {/* Logo & Slogan */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-trophy-line text-lg"></i>
              </div>
              <div>
                <span className="font-heading text-lg font-semibold tracking-wide text-background-50">TENNIS CLUB</span>
                <span className="block font-heading text-xs tracking-[0.2em] uppercase text-accent-500">de Douala</span>
              </div>
            </Link>
            <p className="text-sm text-background-50/60 leading-relaxed mb-6 max-w-sm">{t('footer.slogan')}</p>

            {/* Newsletter */}
            <form data-readdy-form onSubmit={handleNewsletter} className="flex gap-2 max-w-sm">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="flex-1 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-50/40 focus:outline-none focus:border-accent-500/50 transition-colors"
                required
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'loading' ? '...' : status === 'success' ? 'OK' : 'OK'}
              </button>
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2.5">
              {[
                { key: 'nav.home', href: '/' },
                { key: 'nav.activities', href: '/#activities' },
                { key: 'nav.courts', href: '/#courts' },
                { key: 'nav.pricing', href: '/#pricing' },
                { key: 'nav.tournaments', href: '/#news' },
              ].map((link) => (
                <li key={link.key}>
                  <a href={link.href} className="text-sm text-background-50/60 hover:text-accent-400 transition-colors cursor-pointer">{t(link.key)}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* More Links */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">&nbsp;</h4>
            <ul className="space-y-2.5">
              {[
                { key: 'nav.advertisers', href: '/advertisers' },
                { key: 'nav.investors', href: '/investors' },
                { key: 'nav.about', href: '/about' },
                { key: 'nav.contact', href: '/contact' },
              ].map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-sm text-background-50/60 hover:text-accent-400 transition-colors cursor-pointer">
                    {t(link.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Apps */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">{t('footer.followUs')}</h4>
            <div className="flex gap-3 mb-6">
              <a
                href="https://www.facebook.com/profile.php?id=61588341630517"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/10 text-background-50/70 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill"></i>
              </a>
              <a
                href="https://www.instagram.com/tennis_club_douala"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/10 text-background-50/70 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line"></i>
              </a>
              <a
                href="https://wa.me/237687696969"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/10 text-background-50/70 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line"></i>
              </a>
            </div>

            <h4 className="font-heading text-base font-semibold text-background-50 mb-3">{t('footer.mobileApp')}</h4>
            <div className="space-y-2">
              <a
                href="https://play.google.com/store/apps/details?id=com.sharvayaInfotech.CourtBooking"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 text-xs text-background-50/60 hover:text-accent-400 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-google-play-fill"></i>
                </div>
                Google Play
              </a>
              <a
                href="https://apps.apple.com/in/app/tennis-club-de-douala/id6752776130"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 text-xs text-background-50/60 hover:text-accent-400 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-apple-fill"></i>
                </div>
                App Store
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background-50/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-background-50/40">{t('footer.copyright')}</p>
          <div className="flex items-center gap-4">
            <a href="/legal" className="text-xs text-background-50/40 hover:text-background-50/60 transition-colors cursor-pointer">{t('footer.legal')}</a>
            <a href="/privacy" className="text-xs text-background-50/40 hover:text-background-50/60 transition-colors cursor-pointer">{t('footer.privacy')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}